import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../context/AuthContext';
import BRAND from '../brand';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// ── Demo platform-wide data ───────────────────────────────────
const mrrData = [
  { m:'Aug', mrr:1200 },{ m:'Sep', mrr:2800 },{ m:'Oct', mrr:4100 },
  { m:'Nov', mrr:5900 },{ m:'Dec', mrr:7400 },{ m:'Jan', mrr:9700 },
  { m:'Feb', mrr:12400 },
];
const planBreakdown = [
  { name:'Starter', value:8,  color:'#71717a' },
  { name:'Growth',  value:14, color:BRAND.accent },
  { name:'Agency',  value:4,  color:'#10b981' },
];
const revenueByMonth = [
  { m:'Sep', rev:2800 },{ m:'Oct', rev:4100 },{ m:'Nov', rev:5900 },
  { m:'Dec', rev:7400 },{ m:'Jan', rev:9700 },{ m:'Feb', rev:12400 },
];

const NAV = [
  { key:'overview',    icon:'▤',  label:'Overview'        },
  { key:'customers',   icon:'👥', label:'Customers'       },
  { key:'workers',     icon:'💼', label:'Sales Team'      },
  { key:'revenue',     icon:'💰', label:'Revenue'         },
  { key:'bots',        icon:'🤖', label:'All Bots'        },
  { key:'support',     icon:'🎧', label:'Support Tickets' },
  { key:'settings',    icon:'⚙️', label:'Platform Settings'},
];

const Card = ({ children, style }) => (
  <div style={{ background:'#111113', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'1.5rem', ...style }}>{children}</div>
);
const Stat = ({ label, value, sub, color, icon }) => (
  <Card>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem' }}>
      <div style={{ fontSize:'0.68rem', color:'#71717a', fontWeight:600, letterSpacing:'1px', textTransform:'uppercase' }}>{label}</div>
      {icon && <span style={{ fontSize:'1.1rem' }}>{icon}</span>}
    </div>
    <div style={{ fontSize:'1.9rem', fontWeight:700, color:color||'#fafafa', lineHeight:1, fontFamily:'Geist Mono,monospace' }}>{value}</div>
    {sub && <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:'0.4rem' }}>{sub}</div>}
  </Card>
);

const Badge = ({ children, color='#71717a', bg='rgba(113,113,122,0.12)' }) => (
  <span style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.5px', textTransform:'uppercase', color, background:bg, padding:'0.2rem 0.55rem', borderRadius:4 }}>{children}</span>
);

const Btn = ({ children, onClick, variant='primary', small, disabled, style: s }) => {
  const base = { border:'none', borderRadius:8, fontFamily:'Geist,sans-serif', fontWeight:600, cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1, transition:'all 0.15s', whiteSpace:'nowrap', ...s };
  const sizes = small ? { fontSize:'0.75rem', padding:'0.35rem 0.75rem' } : { fontSize:'0.85rem', padding:'0.6rem 1.2rem' };
  const variants = {
    primary: { background:BRAND.accent, color:BRAND.accentText },
    ghost:   { background:'rgba(255,255,255,0.05)', color:'#a1a1aa', border:'1px solid rgba(255,255,255,0.1)' },
    danger:  { background:'rgba(239,68,68,0.1)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.2)' },
    success: { background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.2)' },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...sizes, ...variants[variant] }}>{children}</button>;
};

const inp = { width:'100%', padding:'0.65rem 0.9rem', background:'#18181b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fafafa', fontSize:'0.875rem', fontFamily:'Geist,sans-serif', outline:'none' };

export default function AdminDashboard() {
  const { user, logout, getUsersByRole, createWorker, adminUpdateUser, adminDeleteUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');

  // Customers
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');

  // Workers
  const [workers, setWorkers] = useState([]);
  const [showNewWorker, setShowNewWorker] = useState(false);
  const [workerForm, setWorkerForm] = useState({ name:'', email:'', password:'', commissionRate:'15' });
  const [workerError, setWorkerError] = useState('');
  const [workerSuccess, setWorkerSuccess] = useState('');

  // Support tickets (demo)
  const [tickets] = useState([
    { id:'TK001', customer:'Acme Corp', subject:'Bot giving wrong pricing', priority:'high',   status:'open',     created:'2h ago'  },
    { id:'TK002', customer:'TechFlow',  subject:'Widget not loading on site', priority:'high',   status:'open',     created:'4h ago'  },
    { id:'TK003', customer:'GreenLeaf', subject:'How do I update knowledge base?', priority:'medium', status:'pending',  created:'1d ago'  },
    { id:'TK004', customer:'Nova Dig.',  subject:'Request for white-label',    priority:'low',    status:'resolved', created:'2d ago'  },
    { id:'TK005', customer:'Bright Co', subject:'Escalation not triggering',   priority:'medium', status:'open',     created:'3h ago'  },
  ]);

  // Platform settings
  const [settings, setSettings] = useState({
    adminEmail: 'admin@retflow.ai',
    supportEmail: 'support@retflow.ai',
    defaultCommission: '15',
    trialDays: '14',
    maintenanceMode: false,
    newSignupsEnabled: true,
    anthropicModel: 'claude-opus-4-5',
  });
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    setCustomers(getUsersByRole(ROLES.CUSTOMER));
    setWorkers(getUsersByRole(ROLES.WORKER));
  }, [tab, getUsersByRole]);

  const refreshLists = () => {
    setCustomers(getUsersByRole(ROLES.CUSTOMER));
    setWorkers(getUsersByRole(ROLES.WORKER));
  };

  const totalMRR    = customers.length * 197; // demo average
  const totalBots   = customers.reduce((a, c) => a + (c.bots?.length || 0), 0);
  const activeTrials = customers.filter(c => !c.stripeActivated).length;

  const handleCreateWorker = () => {
    setWorkerError('');
    if (!workerForm.name || !workerForm.email || !workerForm.password) { setWorkerError('All fields required'); return; }
    const res = createWorker(workerForm.name, workerForm.email, workerForm.password, parseFloat(workerForm.commissionRate) / 100);
    if (res.error) { setWorkerError(res.error); return; }
    setWorkerSuccess(`${workerForm.name} added successfully`);
    setWorkerForm({ name:'', email:'', password:'', commissionRate:'15' });
    setShowNewWorker(false);
    refreshLists();
    setTimeout(() => setWorkerSuccess(''), 3000);
  };

  const handleToggleWorker = (workerId, currentActive) => {
    adminUpdateUser(workerId, { active: !currentActive });
    refreshLists();
  };

  const handleDeleteUser = (userId, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    adminDeleteUser(userId);
    refreshLists();
  };

  const handleChangePlan = (customerId, newPlan) => {
    adminUpdateUser(customerId, { plan: newPlan });
    refreshLists();
  };

  const saveSettings = () => {
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  // ── Render panels ────────────────────────────────────────────

  const OverviewPanel = () => (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'2rem' }}>
        <Stat label="Monthly Recurring Revenue" value={`$${(totalMRR).toLocaleString()}`} sub={`${customers.length} paying customers`} color="#10b981" icon="💰" />
        <Stat label="Total Customers"            value={customers.length}                  sub={`${activeTrials} on free trial`}      icon="👥" />
        <Stat label="Sales Workers"              value={workers.length}                    sub="Active sales team"                    icon="💼" />
        <Stat label="Total Bots Deployed"        value={totalBots}                         sub="Across all accounts"                  color={BRAND.accent} icon="🤖" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'1.5rem', marginBottom:'1.5rem' }}>
        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.25rem' }}>MRR Growth</div>
          <div style={{ fontSize:'0.72rem', color:'#71717a', marginBottom:'1.25rem' }}>Monthly recurring revenue over time</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={mrrData} margin={{top:0,right:0,bottom:0,left:-20}}>
              <defs>
                <linearGradient id="mrrg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="m" tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v/1000}k`}/>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}} formatter={v=>[`$${v.toLocaleString()}`, 'MRR']}/>
              <Area type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2} fill="url(#mrrg)" name="MRR"/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.25rem' }}>Plan Breakdown</div>
          <div style={{ fontSize:'0.72rem', color:'#71717a', marginBottom:'1rem' }}>Customers by plan</div>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={planBreakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" stroke="none">
                {planBreakdown.map((e,i) => <Cell key={i} fill={e.color}/>)}
              </Pie>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}}/>
            </PieChart>
          </ResponsiveContainer>
          {planBreakdown.map(p => (
            <div key={p.name} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', marginTop:'0.4rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}><div style={{ width:8, height:8, borderRadius:'50%', background:p.color }}/><span style={{ color:'#a1a1aa' }}>{p.name}</span></div>
              <span style={{ fontWeight:600 }}>{p.value}</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Recent signups */}
      <Card>
        <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1.25rem' }}>Recent Customer Signups</div>
        {customers.length === 0 ? (
          <div style={{ textAlign:'center', color:'#71717a', padding:'2rem', fontSize:'0.85rem' }}>No customers yet — share your landing page to get started</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ fontSize:'0.68rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                {['Name','Email','Plan','Bots','Joined','Status','Actions'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'0.5rem 0.75rem', fontWeight:600, borderBottom:'1px solid rgba(255,255,255,0.06)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.slice(0,8).map(c => (
                <tr key={c.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding:'0.75rem', fontSize:'0.85rem', fontWeight:500 }}>{c.name}</td>
                  <td style={{ padding:'0.75rem', fontSize:'0.82rem', color:'#71717a' }}>{c.email}</td>
                  <td style={{ padding:'0.75rem' }}>
                    <select value={c.plan||'starter'} onChange={e => handleChangePlan(c.id, e.target.value)}
                      style={{ background:'rgba(245,158,11,0.1)', color:BRAND.accent, border:'1px solid rgba(245,158,11,0.2)', borderRadius:6, padding:'0.2rem 0.5rem', fontSize:'0.72rem', fontWeight:700, fontFamily:'Geist,sans-serif', cursor:'pointer', textTransform:'capitalize' }}>
                      <option value="starter">Starter</option>
                      <option value="growth">Growth</option>
                      <option value="agency">Agency</option>
                    </select>
                  </td>
                  <td style={{ padding:'0.75rem', fontSize:'0.82rem', fontFamily:'Geist Mono,monospace' }}>{c.bots?.length || 0}</td>
                  <td style={{ padding:'0.75rem', fontSize:'0.78rem', color:'#71717a' }}>{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding:'0.75rem' }}>
                    <Badge color={c.stripeActivated?'#10b981':'#f59e0b'} bg={c.stripeActivated?'rgba(16,185,129,0.1)':'rgba(245,158,11,0.1)'}>
                      {c.stripeActivated ? 'Active' : 'Trial'}
                    </Badge>
                  </td>
                  <td style={{ padding:'0.75rem' }}>
                    <div style={{ display:'flex', gap:'0.4rem' }}>
                      <Btn small variant="ghost" onClick={() => setSelectedCustomer(c)}>View</Btn>
                      <Btn small variant="danger" onClick={() => handleDeleteUser(c.id, c.name)}>Remove</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );

  const CustomersPanel = () => {
    const filtered = customers.filter(c =>
      c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.email?.toLowerCase().includes(customerSearch.toLowerCase())
    );
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
          <div>
            <h2 style={{ fontSize:'1.1rem', fontWeight:600 }}>Customers</h2>
            <p style={{ fontSize:'0.8rem', color:'#71717a', marginTop:2 }}>{customers.length} total accounts</p>
          </div>
          <input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search by name or email..." style={{ ...inp, width:260 }} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
        </div>

        {selectedCustomer && (
          <Card style={{ marginBottom:'1.5rem', border:`1px solid rgba(245,158,11,0.25)` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
              <div style={{ fontWeight:600, fontSize:'0.95rem' }}>👤 {selectedCustomer.name}</div>
              <Btn small variant="ghost" onClick={() => setSelectedCustomer(null)}>✕ Close</Btn>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'1rem' }}>
              {[
                { l:'Email',   v:selectedCustomer.email },
                { l:'Plan',    v:selectedCustomer.plan || 'starter', capitalize:true },
                { l:'Joined',  v:new Date(selectedCustomer.createdAt).toLocaleDateString() },
                { l:'Status',  v:selectedCustomer.stripeActivated ? 'Paying' : 'Trial' },
              ].map(item => (
                <div key={item.l} style={{ background:'#18181b', borderRadius:8, padding:'0.75rem' }}>
                  <div style={{ fontSize:'0.68rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.3rem' }}>{item.l}</div>
                  <div style={{ fontSize:'0.85rem', fontWeight:500, textTransform:item.capitalize?'capitalize':'none' }}>{item.v}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <Btn variant="ghost" small onClick={() => { adminUpdateUser(selectedCustomer.id, { stripeActivated: !selectedCustomer.stripeActivated }); refreshLists(); setSelectedCustomer(null); }}>
                {selectedCustomer.stripeActivated ? 'Suspend account' : 'Activate account'}
              </Btn>
              <Btn variant="danger" small onClick={() => { handleDeleteUser(selectedCustomer.id, selectedCustomer.name); setSelectedCustomer(null); }}>Delete account</Btn>
            </div>
          </Card>
        )}

        <Card>
          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', color:'#71717a', padding:'3rem', fontSize:'0.85rem' }}>{customerSearch ? 'No customers match your search' : 'No customers yet'}</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ fontSize:'0.68rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {['Customer','Email','Plan','Bots','Revenue','Status','Actions'].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'0.6rem 0.75rem', borderBottom:'1px solid rgba(255,255,255,0.06)', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'0.85rem 0.75rem' }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{c.name}</div>
                    </td>
                    <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.82rem', color:'#71717a' }}>{c.email}</td>
                    <td style={{ padding:'0.85rem 0.75rem' }}>
                      <select value={c.plan||'starter'} onChange={e => { handleChangePlan(c.id, e.target.value); refreshLists(); }}
                        style={{ background:'rgba(245,158,11,0.08)', color:BRAND.accent, border:'1px solid rgba(245,158,11,0.2)', borderRadius:6, padding:'0.2rem 0.5rem', fontSize:'0.72rem', fontWeight:700, fontFamily:'Geist,sans-serif', cursor:'pointer' }}>
                        <option value="starter">Starter $97</option>
                        <option value="growth">Growth $297</option>
                        <option value="agency">Agency $597</option>
                      </select>
                    </td>
                    <td style={{ padding:'0.85rem 0.75rem', fontFamily:'Geist Mono,monospace', fontSize:'0.82rem' }}>{c.bots?.length || 0}</td>
                    <td style={{ padding:'0.85rem 0.75rem', fontFamily:'Geist Mono,monospace', fontSize:'0.82rem', color:'#10b981' }}>
                      ${({ starter:97, growth:297, agency:597 }[c.plan||'starter']||97)}/mo
                    </td>
                    <td style={{ padding:'0.85rem 0.75rem' }}>
                      <Badge color={c.stripeActivated?'#10b981':'#f59e0b'} bg={c.stripeActivated?'rgba(16,185,129,0.1)':'rgba(245,158,11,0.1)'}>
                        {c.stripeActivated ? 'Active' : 'Trial'}
                      </Badge>
                    </td>
                    <td style={{ padding:'0.85rem 0.75rem' }}>
                      <div style={{ display:'flex', gap:'0.4rem' }}>
                        <Btn small variant="ghost" onClick={() => setSelectedCustomer(c)}>Details</Btn>
                        <Btn small variant="danger" onClick={() => handleDeleteUser(c.id, c.name)}>Remove</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    );
  };

  const WorkersPanel = () => (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
        <div>
          <h2 style={{ fontSize:'1.1rem', fontWeight:600 }}>Sales Team</h2>
          <p style={{ fontSize:'0.8rem', color:'#71717a', marginTop:2 }}>Manage your outreach workers</p>
        </div>
        <Btn onClick={() => setShowNewWorker(true)}>+ Add Worker</Btn>
      </div>

      {workerSuccess && <div style={{ background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.2)', borderRadius:8, padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.85rem', color:'#10b981' }}>✓ {workerSuccess}</div>}

      {showNewWorker && (
        <Card style={{ marginBottom:'1.5rem', border:`1px solid rgba(245,158,11,0.2)` }}>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1rem' }}>New Sales Worker Account</div>
          {workerError && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:8, padding:'0.65rem', marginBottom:'0.75rem', fontSize:'0.82rem', color:'#fca5a5' }}>{workerError}</div>}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
            {[
              { label:'Full Name',        key:'name',           type:'text',     placeholder:'Jane Smith' },
              { label:'Email',            key:'email',          type:'email',    placeholder:'jane@retflow.ai' },
              { label:'Temporary Password', key:'password',     type:'password', placeholder:'Min 6 characters' },
              { label:'Commission Rate %', key:'commissionRate', type:'number',  placeholder:'15' },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>{field.label}</label>
                <input type={field.type} value={workerForm[field.key]} onChange={e => setWorkerForm(p => ({ ...p, [field.key]: e.target.value }))}
                  placeholder={field.placeholder} style={inp}
                  onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:'0.75rem' }}>
            <Btn onClick={handleCreateWorker}>Create Account</Btn>
            <Btn variant="ghost" onClick={() => { setShowNewWorker(false); setWorkerError(''); }}>Cancel</Btn>
          </div>
        </Card>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
        {workers.length === 0 ? (
          <Card style={{ textAlign:'center', padding:'3rem', color:'#71717a' }}>
            <div style={{ fontSize:'2rem', marginBottom:'0.75rem' }}>💼</div>
            <div style={{ fontSize:'0.875rem', color:'#a1a1aa', marginBottom:'0.5rem' }}>No workers yet</div>
            <div style={{ fontSize:'0.8rem' }}>Add your first sales rep to start tracking outreach</div>
          </Card>
        ) : workers.map(w => {
          const leads = w.leads || [];
          const closed    = leads.filter(l => l.status === 'closed').length;
          const inProgress = leads.filter(l => ['contacted','piloting'].includes(l.status)).length;
          const commission = closed * 297 * (w.commissionRate || 0.15);
          return (
            <Card key={w.id}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.85rem' }}>
                  <div style={{ width:44, height:44, borderRadius:'50%', background:'rgba(245,158,11,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', fontWeight:700, color:BRAND.accent }}>
                    {w.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:'0.95rem' }}>{w.name}</div>
                    <div style={{ fontSize:'0.75rem', color:'#71717a' }}>{w.email} · {Math.round((w.commissionRate||0.15)*100)}% commission</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <div style={{ fontSize:'0.72rem', color:w.active?'#10b981':'#ef4444', background:w.active?'rgba(16,185,129,0.1)':'rgba(239,68,68,0.1)', padding:'0.2rem 0.55rem', borderRadius:100, border:`1px solid ${w.active?'rgba(16,185,129,0.25)':'rgba(239,68,68,0.25)'}` }}>
                    {w.active ? 'Active' : 'Inactive'}
                  </div>
                  <Btn small variant="ghost" onClick={() => handleToggleWorker(w.id, w.active)}>{w.active ? 'Suspend' : 'Activate'}</Btn>
                  <Btn small variant="danger" onClick={() => handleDeleteUser(w.id, w.name)}>Remove</Btn>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'0.75rem', marginTop:'1.25rem' }}>
                {[
                  { l:'Total Leads',   v:leads.length },
                  { l:'In Progress',   v:inProgress, color:'#f59e0b' },
                  { l:'Closed',        v:closed,      color:'#10b981' },
                  { l:'Close Rate',    v:leads.length ? Math.round((closed/leads.length)*100)+'%' : '—', color:'#3b82f6' },
                  { l:'Commission Earned', v:`$${commission.toFixed(0)}`, color:'#10b981' },
                ].map(s => (
                  <div key={s.l} style={{ background:'#18181b', borderRadius:8, padding:'0.65rem 0.75rem' }}>
                    <div style={{ fontSize:'0.65rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.25rem' }}>{s.l}</div>
                    <div style={{ fontSize:'1.1rem', fontWeight:700, color:s.color||'#fafafa', fontFamily:'Geist Mono,monospace' }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const RevenuePanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Revenue</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Platform-wide financial overview</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'2rem' }}>
        <Stat label="Current MRR"    value="$12,400" sub="↑ 28% vs last month" color="#10b981" />
        <Stat label="ARR Run Rate"   value="$148,800" sub="Annualized" color="#10b981" />
        <Stat label="Avg Revenue/Customer" value="$215" sub="Blended average" />
        <Stat label="Churn This Month" value="2.3%" sub="1 customer lost" color="#ef4444" />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'1.5rem', marginBottom:'1.5rem' }}>
        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.25rem' }}>Monthly Revenue</div>
          <div style={{ fontSize:'0.72rem', color:'#71717a', marginBottom:'1.25rem' }}>Last 6 months</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenueByMonth} margin={{top:0,right:0,bottom:0,left:-20}}>
              <XAxis dataKey="m" tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'#71717a'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v/1000}k`}/>
              <Tooltip contentStyle={{background:'#18181b',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,fontSize:12}} formatter={v=>[`$${v.toLocaleString()}`, 'Revenue']}/>
              <Bar dataKey="rev" fill={BRAND.accent} radius={[4,4,0,0]} name="Revenue ($)"/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1rem' }}>Revenue by Plan</div>
          {[
            { plan:'Agency (4)',  mrr:2388, color:'#10b981' },
            { plan:'Growth (14)', mrr:4158, color:BRAND.accent },
            { plan:'Starter (8)', mrr:776,  color:'#71717a' },
          ].map(r => (
            <div key={r.plan} style={{ marginBottom:'1rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem', marginBottom:'0.35rem' }}>
                <span style={{ color:'#a1a1aa' }}>{r.plan}</span>
                <span style={{ fontWeight:600, fontFamily:'Geist Mono,monospace', color:r.color }}>${r.mrr.toLocaleString()}/mo</span>
              </div>
              <div style={{ height:5, background:'#1e1e24', borderRadius:3 }}>
                <div style={{ height:'100%', width:`${Math.round((r.mrr/7322)*100)}%`, background:r.color, borderRadius:3 }}/>
              </div>
            </div>
          ))}
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.07)', paddingTop:'0.75rem', marginTop:'0.25rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem' }}>
              <span style={{ color:'#71717a' }}>Total MRR</span>
              <span style={{ fontWeight:700, color:'#10b981', fontFamily:'Geist Mono,monospace' }}>$7,322/mo</span>
            </div>
          </div>
        </Card>
      </div>
      <Card>
        <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1rem' }}>Payment Events</div>
        {[
          { event:'New subscription', customer:'Bright Media', plan:'Growth', amount:'+$297', time:'2h ago', type:'positive' },
          { event:'Plan upgraded',    customer:'TechFlow Inc', plan:'Agency', amount:'+$300', time:'1d ago', type:'positive' },
          { event:'Trial converted',  customer:'GreenLeaf Co', plan:'Starter', amount:'+$97', time:'2d ago', type:'positive' },
          { event:'Subscription cancelled', customer:'Momentum', plan:'Growth', amount:'-$297', time:'3d ago', type:'negative' },
          { event:'Payment failed',   customer:'Apex Corp', plan:'Starter', amount:'$0',    time:'4d ago', type:'warning' },
        ].map((e,i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:e.type==='positive'?'#10b981':e.type==='negative'?'#ef4444':'#f59e0b', flexShrink:0 }}/>
              <div>
                <div style={{ fontSize:'0.85rem', fontWeight:500 }}>{e.event} — {e.customer}</div>
                <div style={{ fontSize:'0.72rem', color:'#71717a' }}>{e.plan} plan · {e.time}</div>
              </div>
            </div>
            <span style={{ fontFamily:'Geist Mono,monospace', fontWeight:700, fontSize:'0.875rem', color:e.type==='positive'?'#10b981':e.type==='negative'?'#ef4444':'#f59e0b' }}>{e.amount}</span>
          </div>
        ))}
      </Card>
    </div>
  );

  const BotsPanel = () => {
    const allBots = customers.flatMap(c => (c.bots || []).map(b => ({ ...b, owner: c.name, ownerEmail: c.email })));
    return (
      <div>
        <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>All Bots</h2>
        <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Every bot deployed across all customer accounts</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'1.5rem' }}>
          <Stat label="Total Bots"       value={allBots.length}                                                         />
          <Stat label="Active"           value={allBots.filter(b=>b.active).length}     color="#10b981"                />
          <Stat label="Total Convos"     value={allBots.reduce((a,b)=>a+(b.conversations||0),0).toLocaleString()}       />
          <Stat label="Revenue Attributed" value={`$${allBots.reduce((a,b)=>a+(b.revenue||0),0).toLocaleString()}`} color={BRAND.accent} />
        </div>
        <Card>
          {allBots.length === 0 ? (
            <div style={{ textAlign:'center', color:'#71717a', padding:'3rem', fontSize:'0.85rem' }}>No bots deployed yet — customers need to create bots in their dashboard</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ fontSize:'0.68rem', color:'#71717a', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {['Bot Name','Owner','Company','Conversations','Revenue','Status'].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'0.6rem 0.75rem', borderBottom:'1px solid rgba(255,255,255,0.06)', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allBots.map((bot, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding:'0.85rem 0.75rem', fontWeight:500, fontSize:'0.875rem' }}>{bot.name}</td>
                    <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.82rem', color:'#71717a' }}>{bot.owner}</td>
                    <td style={{ padding:'0.85rem 0.75rem', fontSize:'0.82rem', color:'#71717a' }}>{bot.companyName || '—'}</td>
                    <td style={{ padding:'0.85rem 0.75rem', fontFamily:'Geist Mono,monospace', fontSize:'0.82rem' }}>{(bot.conversations||0).toLocaleString()}</td>
                    <td style={{ padding:'0.85rem 0.75rem', fontFamily:'Geist Mono,monospace', fontSize:'0.82rem', color:'#10b981' }}>${(bot.revenue||0).toLocaleString()}</td>
                    <td style={{ padding:'0.85rem 0.75rem' }}>
                      <Badge color={bot.active?'#10b981':'#ef4444'} bg={bot.active?'rgba(16,185,129,0.1)':'rgba(239,68,68,0.1)'}>{bot.active?'Live':'Paused'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    );
  };

  const SupportPanel = () => {
    const priorityColor = { high:'#ef4444', medium:'#f59e0b', low:'#10b981' };
    const statusColor   = { open:'#ef4444', pending:'#f59e0b', resolved:'#10b981' };
    return (
      <div>
        <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Support Tickets</h2>
        <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Customer issues that need your attention</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem', marginBottom:'1.5rem' }}>
          <Stat label="Open"     value={tickets.filter(t=>t.status==='open').length}     color="#ef4444" />
          <Stat label="Pending"  value={tickets.filter(t=>t.status==='pending').length}  color="#f59e0b" />
          <Stat label="Resolved" value={tickets.filter(t=>t.status==='resolved').length} color="#10b981" />
        </div>
        <Card>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            {tickets.map(t => (
              <div key={t.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', background:'#18181b', border:`1px solid rgba(255,255,255,0.05)`, borderRadius:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.85rem' }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background:priorityColor[t.priority], flexShrink:0 }}/>
                  <div>
                    <div style={{ fontWeight:500, fontSize:'0.875rem' }}>{t.subject}</div>
                    <div style={{ fontSize:'0.72rem', color:'#71717a', marginTop:1 }}>{t.customer} · {t.created} · #{t.id}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <Badge color={priorityColor[t.priority]} bg={`${priorityColor[t.priority]}18`}>{t.priority}</Badge>
                  <Badge color={statusColor[t.status]}   bg={`${statusColor[t.status]}18`}>{t.status}</Badge>
                  <Btn small variant="ghost">Open</Btn>
                  {t.status !== 'resolved' && <Btn small variant="success">Resolve</Btn>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  const SettingsPanel = () => (
    <div>
      <h2 style={{ fontSize:'1.1rem', fontWeight:600, marginBottom:'0.35rem' }}>Platform Settings</h2>
      <p style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1.5rem' }}>Control platform-wide configuration</p>
      <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>Email Configuration</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
            {[
              { label:'Admin Email',   key:'adminEmail',   type:'email' },
              { label:'Support Email', key:'supportEmail', type:'email' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>{f.label}</label>
                <input type={f.type} style={inp} value={settings[f.key]} onChange={e => setSettings(p=>({...p,[f.key]:e.target.value}))} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>Business Rules</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
            {[
              { label:'Default Commission Rate %', key:'defaultCommission', type:'number' },
              { label:'Trial Period (days)',        key:'trialDays',         type:'number' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>{f.label}</label>
                <input type={f.type} style={inp} value={settings[f.key]} onChange={e => setSettings(p=>({...p,[f.key]:e.target.value}))} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
              </div>
            ))}
          </div>
          <div>
            <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>AI Model</label>
            <select style={{ ...inp, width:300, cursor:'pointer' }} value={settings.anthropicModel} onChange={e => setSettings(p=>({...p,anthropicModel:e.target.value}))}>
              <option value="claude-opus-4-5">claude-opus-4-5 (Best quality)</option>
              <option value="claude-sonnet-4-5">claude-sonnet-4-5 (Faster)</option>
              <option value="claude-haiku-4-5-20251001">claude-haiku-4-5 (Cheapest)</option>
            </select>
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>Platform Switches</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            {[
              { label:'New signups enabled',  key:'newSignupsEnabled',  desc:'Allow new customers to sign up on the landing page' },
              { label:'Maintenance mode',     key:'maintenanceMode',    desc:'Show a maintenance page to all visitors' },
            ].map(toggle => (
              <div key={toggle.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem', background:'#18181b', borderRadius:10 }}>
                <div>
                  <div style={{ fontWeight:500, fontSize:'0.875rem' }}>{toggle.label}</div>
                  <div style={{ fontSize:'0.75rem', color:'#71717a', marginTop:2 }}>{toggle.desc}</div>
                </div>
                <div onClick={() => setSettings(p=>({...p,[toggle.key]:!p[toggle.key]}))}
                  style={{ width:44, height:24, borderRadius:12, background:settings[toggle.key]?'#10b981':'#27272a', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:2, left:settings[toggle.key]?22:2, width:20, height:20, borderRadius:'50%', background:'#fafafa', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight:600, fontSize:'0.9rem', marginBottom:'0.5rem' }}>Admin Credentials</div>
          <div style={{ fontSize:'0.8rem', color:'#71717a', marginBottom:'1rem' }}>Change the admin login details. You will need to log in again after saving.</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
            <div>
              <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>Admin Email</label>
              <input type="email" defaultValue="admin@retflow.ai" style={inp} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
            </div>
            <div>
              <label style={{ fontSize:'0.78rem', color:'#a1a1aa', display:'block', marginBottom:'0.4rem' }}>New Password</label>
              <input type="password" placeholder="Leave blank to keep current" style={inp} onFocus={e=>e.target.style.borderColor=BRAND.accent} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
            </div>
          </div>
        </Card>

        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <Btn onClick={saveSettings}>Save all settings</Btn>
          {settingsSaved && <span style={{ fontSize:'0.82rem', color:'#10b981' }}>✓ Settings saved</span>}
        </div>
      </div>
    </div>
  );

  const renderPanel = () => {
    switch(tab) {
      case 'overview':  return <OverviewPanel/>;
      case 'customers': return <CustomersPanel/>;
      case 'workers':   return <WorkersPanel/>;
      case 'revenue':   return <RevenuePanel/>;
      case 'bots':      return <BotsPanel/>;
      case 'support':   return <SupportPanel/>;
      case 'settings':  return <SettingsPanel/>;
      default:          return <OverviewPanel/>;
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#09090b', fontFamily:'Geist,sans-serif', display:'flex' }}>

      {/* SIDEBAR */}
      <aside style={{ width:230, background:'#0a0808', borderRight:'1px solid rgba(255,80,80,0.08)', display:'flex', flexDirection:'column', flexShrink:0, position:'sticky', top:0, height:'100vh' }}>
        <div style={{ padding:'1.5rem 1.25rem', borderBottom:'1px solid rgba(255,80,80,0.08)' }}>
          <div style={{ fontFamily:'Instrument Serif,serif', fontSize:'1.2rem', color:'#fafafa' }}>
            {BRAND.name}<span style={{ color:'#ef4444' }}>.</span>
          </div>
          <div style={{ fontSize:'0.65rem', fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', color:'#ef4444', marginTop:'0.25rem' }}>Admin Panel</div>
        </div>

        <nav style={{ flex:1, padding:'0.75rem 0', overflowY:'auto' }}>
          {NAV.map(item => {
            const active = tab === item.key;
            return (
              <button key={item.key} onClick={() => setTab(item.key)}
                style={{ display:'flex', alignItems:'center', gap:'0.65rem', width:'calc(100% - 1.5rem)', margin:'0.1rem 0.75rem', padding:'0.6rem 1rem', borderRadius:8, cursor:'pointer', border:'none', textAlign:'left', background:active?'rgba(239,68,68,0.12)':'transparent', color:active?'#ef4444':'#71717a', fontSize:'0.85rem', fontWeight:active?600:400, fontFamily:'Geist,sans-serif', transition:'all 0.15s' }}
                onMouseEnter={e => { if(!active){e.currentTarget.style.background='rgba(255,255,255,0.04)';e.currentTarget.style.color='#fafafa';} }}
                onMouseLeave={e => { if(!active){e.currentTarget.style.background='transparent';e.currentTarget.style.color='#71717a';} }}>
                <span style={{ fontSize:'0.9rem', flexShrink:0 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>

        <div style={{ padding:'1rem 1.25rem', borderTop:'1px solid rgba(255,80,80,0.08)' }}>
          <div style={{ fontSize:'0.72rem', color:'#71717a', marginBottom:'0.5rem' }}>Logged in as</div>
          <div style={{ fontSize:'0.85rem', fontWeight:500, color:'#fafafa', marginBottom:'0.75rem' }}>{user?.name}</div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <Btn small variant="ghost" onClick={() => navigate('/dashboard')}>Customer View</Btn>
            <Btn small variant="danger" onClick={logout}>Sign out</Btn>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex:1, overflow:'auto' }}>
        <div style={{ padding:'1.25rem 2rem', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'#09090b', position:'sticky', top:0, zIndex:10 }}>
          <div>
            <h1 style={{ fontSize:'1.1rem', fontWeight:600 }}>{NAV.find(n=>n.key===tab)?.label}</h1>
            <p style={{ fontSize:'0.72rem', color:'#71717a', marginTop:1 }}>RetFlow Admin · Full platform access</p>
          </div>
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
            <div style={{ fontSize:'0.75rem', color:'#ef4444', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', padding:'0.3rem 0.75rem', borderRadius:100 }}>
              🔴 Admin
            </div>
          </div>
        </div>
        <div style={{ padding:'2rem' }}>{renderPanel()}</div>
      </main>
    </div>
  );
}